if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
        cards: '.collection-card',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.cardStyle = this.dataset.cardStyle;
      this.itemSize = this.dataset.itemSize;
      this.columnsDesktop = this.dataset.columnsDesktop;
      let slidesMobile = this.dataset.slidesMobile ? parseInt(this.dataset.slidesMobile, 10) : 0;
      if (Number.isNaN(slidesMobile)) slidesMobile = 0;
      this.slidesMobile = slidesMobile;

      this.sliderInstance = false;

      this.equalizeTimeout = null;
      this.equalizeInFlight = false;
      this.resizeHandlerBound = false;

      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.onchange = this.init.bind(this);

      this.bindEqualizeTriggers();
      this.init();
    }

    bindEqualizeTriggers() {
      const schedule = () => this.scheduleEqualize();

      // Images can change card height (lazy-load). Re-equalize on load/error.
      this.querySelectorAll('img').forEach((img) => {
        if (img.dataset && img.dataset.foxthemeCollectionListEqualizeBound === '1') return;
        if (img.dataset) img.dataset.foxthemeCollectionListEqualizeBound = '1';

        if (img.complete) schedule();
        else {
          img.addEventListener('load', schedule, { passive: true });
          img.addEventListener('error', schedule, { passive: true });
        }
      });

      // Watch for late DOM inserts (e.g. carousel re-renders).
      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => schedule());
        observer.observe(this, { childList: true, subtree: true });
      }

      // Resize should re-measure.
      if (!this.resizeHandlerBound) {
        window.addEventListener(
          'resize',
          () => {
            schedule();
          },
          { passive: true }
        );
        this.resizeHandlerBound = true;
      }
    }

    scheduleEqualize() {
      clearTimeout(this.equalizeTimeout);
      this.equalizeTimeout = setTimeout(() => this.equalizeCardHeights(), 90);
    }

    equalizeCardHeights() {
      // Equalize regardless of breakpoint (swiper-equal-height + variable titles).
      if (this.equalizeInFlight) return;

      const cards = Array.from(this.querySelectorAll(this.selectors.cards));
      if (!cards.length) return;

      this.equalizeInFlight = true;

      // Reset inline min-height before measuring.
      cards.forEach((card) => {
        card.style.minHeight = '';
      });

      requestAnimationFrame(() => {
        let maxHeight = 0;
        cards.forEach((card) => {
          maxHeight = Math.max(maxHeight, card.getBoundingClientRect().height);
        });

        cards.forEach((card) => {
          card.style.minHeight = `${maxHeight}px`;
        });

        // If slides don't inherit the height, make them follow as well.
        cards.forEach((card) => {
          const slide = card.closest('.swiper-slide');
          if (slide) slide.style.height = `${maxHeight}px`;
        });

        this.equalizeInFlight = false;
      });
    }

    init() {
      if (FoxTheme.config.mqlMobile && !this.slidesMobile) {
        this.destroySlider();
      } else {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.params.spaceBetween = this.getSpaceBetween();
          // Mobile: fisso a 2 per evitare larghezze "a contenuto" che rompono il wrap.
          this.sliderInstance.slider.params.slidesPerView = FoxTheme.config.mqlMobile ? this.slidesMobile || 2 : 'auto';
          this.sliderInstance.slider.params.slidesOffsetAfter = this.getSpaceBetween();
          this.sliderInstance.slider.update();
          this.sliderInstance.slider.slideTo(0, 0, false);
          this.scheduleEqualize();
          return;
        }
        this.initSlider();
      }
    }

    getSpaceBetween() {
      if (!this.sliderWrapper) return 0;
      const styles = window.getComputedStyle(this.sliderWrapper);
      const cssVarGap = styles.getPropertyValue('--f-column-gap');
      const columnGap = cssVarGap || styles.getPropertyValue('column-gap') || styles.getPropertyValue('gap');
      if (!columnGap) return 0;

      const value = columnGap.trim();
      const parsed = Number.parseFloat(value);
      if (Number.isNaN(parsed)) return 0;

      if (value.endsWith('rem')) {
        const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
        return parsed * rootFontSize;
      }
      return parsed;
    }

    initSlider() {
      const spaceBetween = this.getSpaceBetween();
      const sliderOptions = {
        modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Pagination, FoxTheme.Swiper.Mousewheel],
        // Mobile: fisso a 2 per evitare larghezze "a contenuto" che rompono il wrap.
        slidesPerView: this.slidesMobile || 2,
        breakpoints: {
          // Tablet centrale: lascia che Swiper calcoli in base alla viewport
          768: {
            slidesPerView: 'auto',
          },
          // Desktop: lascia gestire a Swiper (come già accadeva prima)
          1024: {
            slidesPerView: 'auto',
          },
        },
        spaceBetween: spaceBetween,
        slidesOffsetAfter: spaceBetween,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        loop: false,
        threshold: 2,
        watchOverflow: true,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();
        this.sliderInstance.slider.slideTo(0, 0, false);

        this.scheduleEqualize();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.sliderInstance.slider.slideTo(index);
          });
        }
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance !== 'object') return;
      this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }
  }
  customElements.define('collection-list', CollectionList);
}
